import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import Markdown from "markdown-to-jsx";
import Layout from "../components/Layout";
import Stage from "../components/Stage/Stage";
import Container from "../components/share/Container/Container";
import Button from "../components/share/Button/Button";
import Link from "../components/share/Link/Link";
import "./region-page.scss";

// gatsby-remark-relative-images' fmImagesToRelative() (run in gatsby-node.js)
// rewrites frontmatter paths that resolve to a file-system page (e.g. /poolbau,
// /contact) into relative paths like "../poolbau". Those resolve incorrectly
// under Gatsby's trailing-slash URLs, so force every internal target back to an
// absolute path here.
const toInternalHref = (p) => {
  if (!p) return p;
  if (/^(https?:)?\/\//.test(p) || /^(mailto|tel):/.test(p)) return p;
  return "/" + p.replace(/^(\.\.?\/)+/, "").replace(/^\/+/, "");
};

export const RegionPageTemplate = ({
  title,
  image,
  intro,
  eyebrow,
  highlights,
  content,
  related,
}) => {
  return (
    <div className="RegionPage">
      <div className="RegionPage__stage">
        <Stage title={title} image={image} description={intro} />
      </div>

      {(eyebrow || (highlights && highlights.length > 0)) && (
        <section className="RegionPage__highlights">
          <Container variant="no-top">
            {eyebrow && <span className="RegionPage__eyebrow">{eyebrow}</span>}
            {highlights && highlights.length > 0 && (
              <ul className="RegionHighlights">
                {highlights.map((h, i) => (
                  <li key={i} className="RegionHighlights__item">
                    {h}
                  </li>
                ))}
              </ul>
            )}
          </Container>
        </section>
      )}

      {content && (
        <section className="RegionPage__body">
          <Container variant="no-top">
            <Markdown>{content}</Markdown>
          </Container>
        </section>
      )}

      {related && related.length > 0 && (
        <section className="RegionPage__related">
          <Container variant="no-top">
            <h2 className="RegionPage__related-title">
              Poolbau in Ihrer Region &amp; passende Leistungen
            </h2>
            <ul className="RegionRelated">
              {related.map((r, i) => (
                <li key={i} className="RegionRelated__item">
                  <Link link={toInternalHref(r.path)} label={r.label} />
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}

      <div className="RegionPage__cta">
        <Container variant="no-top">
          <Button
            variant="dark"
            link="/contact"
            label="Kostenlose Beratung anfragen"
          />
        </Container>
      </div>
    </div>
  );
};

RegionPageTemplate.propTypes = {
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string,
  intro: PropTypes.string,
  eyebrow: PropTypes.string,
  highlights: PropTypes.array,
  content: PropTypes.string,
  related: PropTypes.array,
};

const RegionPage = ({ data, location }) => {
  const { frontmatter } = data.markdownRemark;

  return (
    <Layout
      pageTitle={frontmatter.metaTitle}
      pageDescription={frontmatter.metaDescription}
      pagePath={location && location.pathname}
    >
      <RegionPageTemplate
        image={frontmatter.image}
        title={frontmatter.title}
        intro={frontmatter.intro}
        eyebrow={frontmatter.eyebrow}
        highlights={frontmatter.highlights}
        content={frontmatter.content}
        related={frontmatter.related}
      />
    </Layout>
  );
};

RegionPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
    }),
  }),
};

export default RegionPage;

// Multiple pages share this template, so query the specific node by its id
// (passed via context in gatsby-node.js) instead of by templateKey.
export const RegionPageQuery = graphql`
  query RegionPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      frontmatter {
        title
        metaTitle
        metaDescription
        intro
        eyebrow
        highlights
        content
        related {
          label
          path
        }
        image {
          childImageSharp {
            gatsbyImageData(
              placeholder: BLURRED
              formats: [AUTO, WEBP]
              width: 2000
              breakpoints: [480, 568, 800, 1024, 1280]
              quality: 90
            )
          }
        }
      }
    }
  }
`;
